/**
 * ERMKickstart — AI Risk Essentials.
 *
 * Five-step / 7-day workflow:
 *   Day 1     · Step 1 · Company Profiling (industry + size + geo + assets)
 *   Day 2-3   · Step 2 · AI Aggregation & Sifting (loading state with copy)
 *   Day 4     · Step 3 · Benchmarking & Risk Mapping (pick risks)
 *   Day 5     · Step 4 · Registry Generation (formatted table)
 *   Day 6-7   · Step 5 · Review & Heatmap (interactive likelihood × impact)
 *
 * The visible <WorkflowTimeline /> at the top frames the entire experience
 * so the user understands they are walking through the same five days
 * Diligent describes in its marketing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Landmark, Fuel, Building2, Cpu, HeartPulse, ShoppingBag, Factory, Truck,
  Radio, Crown, GraduationCap, Plane, CreditCard, Brain, Wheat,
  ChevronRight, ChevronLeft, Loader2, Sparkles, Check, Download,
  Globe2, Layers3, ListChecks, FileText, FileSpreadsheet, Target, FilePlus,
} from 'lucide-react';
import { API_BASE } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import WorkflowTimeline from '@/components/WorkflowTimeline';
import RiskHeatmap from '@/components/RiskHeatmap';
import DocumentUploader from '@/components/DocumentUploader';

const ICON_MAP = {
  Landmark, Fuel, Building2, Cpu, HeartPulse, ShoppingBag, Factory, Truck,
  Radio, Crown, GraduationCap, Plane, CreditCard, Brain, Wheat,
};
const SEV_COLORS = ['', 'bg-slate-100 text-slate-600', 'bg-emerald-100 text-emerald-700',
                    'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700',
                    'bg-rose-100 text-rose-700'];
const CAT_TINT = {
  STRATEGIC: 'bg-indigo-50 text-indigo-700',
  OPERATIONAL: 'bg-amber-50 text-amber-700',
  FINANCIAL: 'bg-emerald-50 text-emerald-700',
  REGULATORY: 'bg-violet-50 text-violet-700',
  CYBER: 'bg-rose-50 text-rose-700',
  ESG: 'bg-lime-50 text-lime-700',
  GEOPOLITICAL: 'bg-cyan-50 text-cyan-700',
  PEOPLE: 'bg-sky-50 text-sky-700',
};

export default function ERMKickstart() {
  // step 1..5 maps directly to the 5-step workflow
  const [step, setStep] = useState(1);
  const [workflow, setWorkflow] = useState([]);
  const [catalog, setCatalog] = useState({ industries: [], company_sizes: [], geographies: [], asset_sizes: [] });

  // Day-1 profiling inputs
  const [industry, setIndustry] = useState(null);
  const [companySize, setCompanySize] = useState('growth');
  const [geographies, setGeographies] = useState([]);
  const [assetSize, setAssetSize] = useState(null);

  // Day-2/3 + Day-4 risk benchmarking
  const [risks, setRisks] = useState([]);
  const [selectedRisks, setSelectedRisks] = useState([]);

  // Day-1 supporting documents (multi-file upload, ephemeral text extraction)
  const [docSessionId, setDocSessionId] = useState(null);
  const [docFiles, setDocFiles] = useState([]);

  // Day-5 registry
  const [registry, setRegistry] = useState(null);     // {registry_id, rows: [...]}
  const [highlightId, setHighlightId] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/erm/industries').then(({ data }) => setCatalog(data));
    api.get('/erm/workflow').then(({ data }) => setWorkflow(data.steps));
  }, []);

  const toggleGeo = (id) =>
    setGeographies((cur) => (cur.includes(id) ? cur.filter((g) => g !== id) : [...cur, id]));
  const toggleRisk = (id) =>
    setSelectedRisks((cur) => (cur.includes(id) ? cur.filter((r) => r !== id) : [...cur, id]));

  /** Day-1 → Day-2/3 transition: kicks off the AI 10-K scan */
  const beginAggregation = async () => {
    if (!industry) return toast.error('Pick an industry to start.');
    if (geographies.length === 0) return toast.error('Pick at least one geography.');
    if (!assetSize) return toast.error('Pick an asset size band.');
    setStep(2);
    setLoading(true);
    try {
      const { data } = await api.post('/erm/risks-for-industry', {
        industry: industry.id,
        company_size: companySize,
        geographies,
        asset_size: assetSize,
        document_session_id: docSessionId || null,
      });
      setRisks(data.risks);
      // Auto-advance to Day-4 once the AI returns
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI aggregation failed.');
      setStep(1);
    } finally { setLoading(false); }
  };

  /** Day-4 → Day-5 transition: materialises the formal Risk Registry */
  const generateRegistry = async () => {
    if (selectedRisks.length < 1) return toast.error('Pick at least one risk to draft a registry.');
    setLoading(true);
    setStep(4);
    try {
      const titles = risks.filter((r) => selectedRisks.includes(r.id)).map((r) => r.title);
      const { data } = await api.post('/erm/registry', {
        industry: industry.id,
        company_size: companySize,
        geographies,
        asset_size: assetSize,
        selected_risks: titles,
        document_session_id: docSessionId || null,
      });
      setRegistry(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI registry generation failed.');
      setStep(3);
    } finally { setLoading(false); }
  };

  /** Day-5 → Day-6/7 transition */
  const reviewHeatmap = () => {
    if (!registry?.rows?.length) return;
    setStep(5);
  };

  /** Day-6/7 — persist a single row's adjusted likelihood + impact */
  const updateScore = async (riskId, field, value) => {
    if (!registry) return;
    // Optimistic local update first so the UI feels instant
    const updatedRows = registry.rows.map((r) =>
      r.risk_id === riskId ? { ...r, [field]: value } : r,
    );
    setRegistry({ ...registry, rows: updatedRows });
    const row = updatedRows.find((r) => r.risk_id === riskId);
    try {
      await api.patch(`/erm/registry/${registry.registry_id}/score`, {
        risk_id: riskId,
        likelihood: row.likelihood,
        impact: row.impact,
      });
    } catch (e) {
      toast.error('Could not save score.');
    }
  };

  const startOver = () => {
    setStep(1);
    setIndustry(null);
    setCompanySize('growth');
    setGeographies([]);
    setAssetSize(null);
    setRisks([]);
    setSelectedRisks([]);
    setRegistry(null);
    setHighlightId(null);
    setDocSessionId(null);
    setDocFiles([]);
  };

  return (
    <div className="ve-section space-y-6">
      {/* Page header */}
      <div>
        <div className="ve-eyebrow flex items-center gap-2">
          <Sparkles size={14} className="text-[#8B3FC5]"/>
          AI Risk Essentials
        </div>
        <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
          Turn 120,000 SEC 10-K filings <span className="ve-gold-text">into your risk registry.</span>
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          The core process uses machine learning to read thousands of pages of public SEC data
          and turn them into a functional risk-management framework — for your industry,
          your geography, and your balance-sheet tier.
        </p>
      </div>

      <WorkflowTimeline steps={workflow} active={step} loading={loading && step === 2}/>

      {/* ─────────── STEP 1 — Company Profiling ─────────── */}
      {step === 1 && (
        <div className="ve-card p-6 space-y-7" data-testid="erm-step1">
          <header className="flex items-start gap-3">
            <Layers3 size={22} className="text-[#5E1F8B] shrink-0 mt-0.5"/>
            <div>
              <h2 className="display text-xl font-bold">Day 1 — Company Profiling</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                Tell us your industry sector, geographic footprint, and asset size.
                This establishes the baseline for what peer companies the AI should look at.
              </p>
            </div>
          </header>

          {/* Industry */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
              Industry sector
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {catalog.industries.map((ind) => {
                const Icon = ICON_MAP[ind.icon] || Sparkles;
                const isActive = industry?.id === ind.id;
                return (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind)}
                    data-testid={`erm-industry-${ind.id}`}
                    className={`rounded-lg border p-4 text-left transition-all ${
                      isActive
                        ? 'border-[#8B3FC5] bg-[#FFF8E5] shadow-[0_0_25px_-12px_rgba(174,82,234,0.6)]'
                        : 'border-slate-200 hover:border-[#8B3FC5]/60 hover:bg-[#FFF8E5]/30'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-[#5E1F8B]' : 'text-slate-500'}/>
                    <div className="mt-3 text-sm font-bold leading-tight">{ind.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Company size */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
              Company size
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {catalog.company_sizes.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setCompanySize(s.id)}
                  data-testid={`erm-size-${s.id}`}
                  className={`rounded-md border px-4 py-3 text-left text-sm font-bold transition-all ${
                    companySize === s.id
                      ? 'border-[#8B3FC5] bg-[#FFF8E5]'
                      : 'border-slate-200 hover:border-[#8B3FC5]/60'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Geographic footprint */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-2">
              <Globe2 size={11} className="text-[#5E1F8B]"/>
              Geographic footprint <span className="text-slate-400 normal-case tracking-normal font-normal">(pick all that apply)</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {catalog.geographies.map((g) => {
                const on = geographies.includes(g.id);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGeo(g.id)}
                    data-testid={`erm-geo-${g.id}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                      on
                        ? 'border-[#8B3FC5] bg-[#FFF8E5] text-[#5E1F8B]'
                        : 'border-slate-200 hover:border-[#8B3FC5]/60 text-slate-700'
                    }`}
                  >
                    {on && <Check size={11} className="inline -mt-0.5 mr-1"/>}
                    {g.label}
                  </button>
                );
              })}
            </div>
            {geographies.length > 0 && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-2">
                {geographies.length} region{geographies.length === 1 ? '' : 's'} selected
              </div>
            )}
          </div>

          {/* Asset size */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
              Asset size band
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {catalog.asset_sizes.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setAssetSize(a.id)}
                  data-testid={`erm-asset-${a.id}`}
                  className={`rounded-md border px-4 py-3 text-left text-sm font-bold transition-all ${
                    assetSize === a.id
                      ? 'border-[#8B3FC5] bg-[#FFF8E5]'
                      : 'border-slate-200 hover:border-[#8B3FC5]/60'
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Supporting documents (optional) */}
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-2">
              <FilePlus size={11} className="text-[#5E1F8B]"/>
              Supporting documents <span className="text-slate-400 normal-case tracking-normal font-normal">(optional — but recommended)</span>
            </h3>
            <DocumentUploader
              sessionId={docSessionId}
              onSessionReady={(id, files) => { setDocSessionId(id); setDocFiles(files); }}
              onClear={() => { setDocSessionId(null); setDocFiles([]); }}
            />
          </div>

          <div className="flex justify-end pt-2 border-t border-slate-200">
            <button
              onClick={beginAggregation}
              disabled={!industry || geographies.length === 0 || !assetSize || loading}
              data-testid="erm-begin-aggregation"
              className="ve-btn-primary text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>}
              Begin AI aggregation <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* ─────────── STEP 2 — AI Aggregation & Sifting ─────────── */}
      {step === 2 && (
        <div className="ve-card p-8 text-center" data-testid="erm-step2">
          <div className="mx-auto h-16 w-16 rounded-full border-2 border-[#8B3FC5] flex items-center justify-center bg-[#FFF8E5]">
            <Loader2 size={32} className="text-[#5E1F8B] animate-spin"/>
          </div>
          <h2 className="display text-xl font-bold mt-5">Day 2 – 3 — AI Aggregation & Sifting</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-xl mx-auto">
            Scanning Diligent&rsquo;s library of <b>120,000+ SEC 10-K filings</b> for the Item 1A
            Risk Factors that match your profile.  This usually finishes in ~20 seconds — we
            condense what would have taken your team two days of manual reading.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8B3FC5] animate-pulse"/>
            Live · scanning peer filings
          </div>
        </div>
      )}

      {/* ─────────── STEP 3 — Benchmarking & Risk Mapping ─────────── */}
      {step === 3 && (
        <div className="ve-card p-6" data-testid="erm-step3">
          <header className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <Target size={22} className="text-[#5E1F8B] shrink-0 mt-0.5"/>
              <div>
                <h2 className="display text-xl font-bold">Day 4 — Benchmarking & Risk Mapping</h2>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  We filtered out the noise and grouped the surface-level disclosures.  Pick
                  the risks that map to your <b>{industry?.label}</b> business — the 5-10
                  most relevant typically work best.
                </p>
              </div>
            </div>
            <button
              onClick={() => setStep(1)}
              className="text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
              data-testid="erm-edit-profile"
            >
              <ChevronLeft size={12} className="inline"/> Edit profile
            </button>
          </header>

          <div className="space-y-2">
            {risks.map((r) => {
              const isSel = selectedRisks.includes(r.id);
              const sev = r.severity_baseline || 3;
              return (
                <button
                  key={r.id}
                  onClick={() => toggleRisk(r.id)}
                  data-testid={`erm-risk-${r.id}`}
                  className={`w-full text-left rounded-md border p-4 transition-all ${
                    isSel
                      ? 'border-[#8B3FC5] bg-[#FFF8E5]'
                      : 'border-slate-200 hover:border-[#8B3FC5]/60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                      isSel ? 'border-[#8B3FC5] bg-[#8B3FC5]' : 'border-slate-300 bg-white'
                    }`}>
                      {isSel && <Check size={14} className="text-black"/>}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm">{r.title}</span>
                        <span className={`text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${CAT_TINT[r.category] || 'bg-slate-100 text-slate-600'}`}>
                          {r.category}
                        </span>
                        <span className={`text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${SEV_COLORS[sev] || SEV_COLORS[3]}`}>
                          Sev {sev}/5
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{r.description}</p>
                      {r.evidence_citation && (
                        <p className="text-[10px] text-slate-400 mt-1 italic">{r.evidence_citation}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-[0.18em]">
              {selectedRisks.length} selected
            </span>
            <button
              onClick={generateRegistry}
              disabled={selectedRisks.length === 0 || loading}
              data-testid="erm-generate-registry"
              className="ve-btn-primary text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin"/> : <FileSpreadsheet size={16}/>}
              Generate risk registry <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* ─────────── STEP 4 — Registry Generation ─────────── */}
      {step === 4 && (
        <div className="ve-card p-6" data-testid="erm-step4">
          <header className="flex items-start justify-between gap-3 mb-5">
            <div className="flex items-start gap-3">
              <FileText size={22} className="text-[#5E1F8B] shrink-0 mt-0.5"/>
              <div>
                <h2 className="display text-xl font-bold">Day 5 — Registry Generation</h2>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Your pre-formatted ERM Risk Registry.  Instead of a blank spreadsheet you
                  get a list of targeted risks tailored to your business, complete with
                  suggested descriptions, owners, and mitigations.
                </p>
              </div>
            </div>
            <button
              onClick={() => setStep(3)}
              className="text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
              data-testid="erm-edit-risks"
            >
              <ChevronLeft size={12} className="inline"/> Edit risks
            </button>
          </header>

          {loading && !registry && (
            <div className="py-12 text-center">
              <Loader2 size={32} className="animate-spin mx-auto text-[#5E1F8B]"/>
              <div className="mt-3 text-sm text-slate-500">
                Drafting registry rows with AI…
              </div>
            </div>
          )}

          {registry && (
            <>
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm" data-testid="erm-registry-table">
                  <thead className="bg-slate-50">
                    <tr className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Risk</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Owner</th>
                      <th className="px-3 py-2 text-left">Baseline L × I</th>
                      <th className="px-3 py-2 text-left">Suggested mitigations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registry.rows.map((r, i) => (
                      <tr key={r.risk_id} className="border-t border-slate-200 align-top"
                          data-testid={`erm-registry-row-${r.risk_id}`}>
                        <td className="px-3 py-3 text-slate-400 font-bold">{i + 1}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold">{r.title}</span>
                            {(r.sources || []).length > 0 && (
                              <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#5E1F8B] bg-[#FFF8E5] border border-[#8B3FC5]/40 px-1.5 py-0.5 rounded"
                                    data-testid={`erm-row-sourced-${r.risk_id}`}
                                    title={`Cited from ${(r.sources || []).map(s => s.filename).join(', ')}`}>
                                Sourced · {(r.sources || []).length}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-1 leading-snug max-w-[28rem]">{r.description}</p>
                          {(r.sources || []).length > 0 && (
                            <ul className="mt-2 space-y-1 max-w-[28rem]" data-testid={`erm-row-sources-${r.risk_id}`}>
                              {r.sources.map((s, idx) => (
                                <li key={idx} className="text-[11px] text-slate-500 italic leading-snug">
                                  <span className="text-[#5E1F8B] font-bold not-italic">↳ {s.filename}</span>{' '}
                                  <span className="text-slate-500">&ldquo;{s.snippet}&rdquo;</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded ${CAT_TINT[r.category] || 'bg-slate-100 text-slate-600'}`}>
                            {r.category}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-xs">{r.suggested_owner}</td>
                        <td className="px-3 py-3 text-xs whitespace-nowrap">
                          L{r.baseline_likelihood} × I{r.baseline_impact}
                        </td>
                        <td className="px-3 py-3">
                          <ul className="text-xs text-slate-700 space-y-1">
                            {(r.mitigations || []).map((m, idx) => (
                              <li key={idx} className="flex gap-1.5">
                                <span className="text-[#5E1F8B]">·</span>
                                <span>{m}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-[0.18em]">
                  {registry.rows.length} risks registered
                </div>
                <button
                  onClick={reviewHeatmap}
                  data-testid="erm-go-heatmap"
                  className="ve-btn-primary text-sm"
                >
                  Review & plot heatmap <ChevronRight size={14}/>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─────────── STEP 5 — Review & Heatmap Visualization ─────────── */}
      {step === 5 && registry && (
        <HeatmapStep
          registry={registry}
          highlightId={highlightId}
          setHighlightId={setHighlightId}
          updateScore={updateScore}
          onBack={() => setStep(4)}
          onRestart={startOver}
        />
      )}
    </div>
  );
}

/** Day-6/7 — interactive review.  Renders the heatmap alongside an editable
 *  list of registry rows so the user can adjust likelihood/impact and see
 *  the dot reposition live.
 */
function HeatmapStep({ registry, highlightId, setHighlightId, updateScore, onBack, onRestart }) {
  // Group registry by risk-tier for the right-hand summary
  const summary = useMemo(() => {
    const buckets = { critical: 0, high: 0, moderate: 0, low: 0 };
    (registry.rows || []).forEach((r) => {
      const s = r.likelihood * r.impact;
      if (s >= 15) buckets.critical += 1;
      else if (s >= 10) buckets.high += 1;
      else if (s >= 5) buckets.moderate += 1;
      else buckets.low += 1;
    });
    return buckets;
  }, [registry]);

  return (
    <div className="ve-card p-6 space-y-6" data-testid="erm-step5">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <ListChecks size={22} className="text-[#5E1F8B] shrink-0 mt-0.5"/>
          <div>
            <h2 className="display text-xl font-bold">Day 6 – 7 — Review & Heatmap</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Adjust each risk&rsquo;s likelihood and impact based on internal context.  The dot on
              the heatmap moves live so you can see how your portfolio shifts before taking it
              to the board.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onBack}
                  className="text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
                  data-testid="erm-edit-registry">
            <ChevronLeft size={12} className="inline"/> Edit registry
          </button>
          <button onClick={onRestart}
                  className="text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
                  data-testid="erm-restart">
            Start over
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Heatmap */}
        <div className="lg:col-span-5 flex flex-col">
          <div className="rounded-md border border-slate-200 p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
              Likelihood × Impact heatmap
            </div>
            <RiskHeatmap
              rows={registry.rows}
              highlightId={highlightId}
              onSelect={(id) => setHighlightId(id === highlightId ? null : id)}
            />
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <Pill colour="#F19090" label="Critical (15-25)" count={summary.critical}/>
              <Pill colour="#FBD9B5" label="High (10-14)" count={summary.high}/>
              <Pill colour="#FBF1C2" label="Moderate (5-9)" count={summary.moderate}/>
              <Pill colour="#D9F3DE" label="Low (1-4)" count={summary.low}/>
            </div>
          </div>
        </div>

        {/* Editable list */}
        <div className="lg:col-span-7 space-y-3" data-testid="erm-heatmap-rows">
          {registry.rows.map((r) => {
            const score = r.likelihood * r.impact;
            const tier = score >= 15 ? 'Critical'
              : score >= 10 ? 'High'
                : score >= 5 ? 'Moderate' : 'Low';
            return (
              <div
                key={r.risk_id}
                onMouseEnter={() => setHighlightId(r.risk_id)}
                onMouseLeave={() => setHighlightId(null)}
                className={`rounded-md border p-4 transition-all ${
                  highlightId === r.risk_id
                    ? 'border-[#8B3FC5] bg-[#FFF8E5]/40 shadow-[0_0_18px_-6px_rgba(174,82,234,0.4)]'
                    : 'border-slate-200'
                }`}
                data-testid={`erm-heatmap-row-${r.risk_id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-bold text-sm">{r.title}</div>
                  <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded ${
                    tier === 'Critical' ? 'bg-rose-100 text-rose-700'
                      : tier === 'High' ? 'bg-pink-100 text-pink-700'
                        : tier === 'Moderate' ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {tier} · {score}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <ScoreSlider
                    label="Likelihood"
                    value={r.likelihood}
                    onChange={(v) => updateScore(r.risk_id, 'likelihood', v)}
                    testId={`erm-likelihood-${r.risk_id}`}
                  />
                  <ScoreSlider
                    label="Impact"
                    value={r.impact}
                    onChange={(v) => updateScore(r.risk_id, 'impact', v)}
                    testId={`erm-impact-${r.risk_id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Heatmap is auto-saved each time you adjust a score.
        </div>
        <button
          onClick={async () => {
            try {
              const r = await fetch(`${API_BASE}/erm/registry/${registry.registry_id}/pdf`, {
                credentials: 'include',
              });
              if (!r.ok) {
                const txt = await r.text();
                toast.error(`Export failed: ${r.status}${txt ? ' · ' + txt.slice(0, 100) : ''}`);
                return;
              }
              const blob = await r.blob();
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `ERM-Risk-Registry-${registry.registry_id.slice(-8)}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(a.href);
              toast.success('PDF exported.');
            } catch (e) {
              toast.error(e?.message || 'Export failed.');
            }
          }}
          data-testid="erm-export-pdf"
          className="ve-btn-secondary text-xs"
        >
          <Download size={12}/> Export for board
        </button>
      </div>
    </div>
  );
}

function ScoreSlider({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</span>
        <span className="text-sm font-bold text-[#5E1F8B]">{value}/5</span>
      </div>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            data-testid={`${testId}-${n}`}
            className={`h-7 flex-1 rounded text-xs font-bold transition-all ${
              n <= value
                ? 'bg-[#8B3FC5] text-white'
                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function Pill({ colour, label, count }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-3 w-3 rounded" style={{ background: colour }}/>
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">{label}</span>
      <span className="text-xs font-bold ml-auto">{count}</span>
    </div>
  );
}
